import React, { useState, useEffect } from "react";
import { Link, Routes, Route, useNavigate } from "react-router-dom";
import "../orders/orders.scss";
import "react-datepicker/dist/react-datepicker.css";
import { NotFound } from "../../components/common/NotFound";
import DeleteModal from "../../components/common/DeleteModal";
import Avatar from "../../components/common/Avatar";
import Chip from "../../components/common/Chip";
import filterrSvg from "../.././assets/images/funnel.svg";
import FetchData, { DeleteEntry, GetList, PaginationFetchData } from "../../providers/dataProvider";
import OrderGrid from "../../components/orders/OrderGrid";
import ErrorMessage from "../../components/common/ErrorMessage";
import SaveSuccessModel from "../../components/common/SaveSuccessModel";
import { ErrorMessages, SuccessMessages } from "../../constants/MessageConstants";
import { CheckNull } from "../../utils/Validate";
import { SearchComponent } from "../../components/common/SearchComponent";
import { useRolePermission } from "../../hooks/useRolePermission";
import AdvancedFilterDropdown from "../../components/common/AdvancedFilterDropdown";
import OrderSummary from "../../components/orders/OrderSummary";

const OrderList = () => {
  const navigate = useNavigate();
  const { menuRights } = useRolePermission("Order");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const [orderRef, setOrderRef] = useState(null);
  const [project, setProject] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [distributor, setDistributor] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({ orderRef: null, project: null, customer: null, distributor: null });
  const [deleteShow, setDeleteShow] = useState({ show: false, item: "", title: '', textCode: '', text: '', loading: false });
  const [createMsg, setCreateMsg] = useState(null)
  const [successShow, setSuccessShow] = useState(false)
  const [errorShow, setErrorShow] = useState({ show: false, errorMessage: "" });
  const [displayOrderList, setDisplayOrderList] = useState(null);
  const [allOrders, setAllOrders] = useState([]);

  const initialFilterState = {
    PageNumber: 1, PageSize: 25, OrderReference: "", Project: "", Customer: "", Distributor: "", userId: "", userType: ""
  };

  const [filterData, setFilterData] = useState(initialFilterState);

  const { data: countryData } = FetchData("country");
  const { data: distributorData } = FetchData("distributor");
  const { data: projectData } = FetchData("project");
  const { data: customerData } = FetchData("Customer");
  const { data: currencyData } = FetchData("currency");
  const { data: orderData } = FetchData("order");
  const { data: assignedEngineerList } = FetchData("userprofile");
  const { data: orderList, loading } = PaginationFetchData("order", { ...filterData, PageNumber: 1, PageSize: 10000 });

  useEffect(() => {
    let list = orderList?.payload ? [...orderList.payload] : [];
    const same = (a, b) => a != null && b != null && String(a) === String(b);

    if (appliedFilters.orderRef) list = list.filter(o => same(o.id, appliedFilters.orderRef.id ?? appliedFilters.orderRef.value));
    if (appliedFilters.project) list = list.filter(o => same(o.projectId, appliedFilters.project.id ?? appliedFilters.project.value));
    if (appliedFilters.customer) list = list.filter(o => same(o.customerId, appliedFilters.customer.id ?? appliedFilters.customer.value));
    if (appliedFilters.distributor) list = list.filter(o => same(o.distributorId, appliedFilters.distributor.id ?? appliedFilters.distributor.value));

    const text = (filterData.OrderReference || "").toLowerCase();
    if (text) list = list.filter(o => (o.orderReference || "").toLowerCase().includes(text));

    list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    setAllOrders(list);
  }, [orderList, appliedFilters, filterData.OrderReference]);

  useEffect(() => {
    const startIndex = (pageNumber - 1) * pageSize;
    const pagePayload = allOrders.slice(startIndex, startIndex + pageSize);
    setDisplayOrderList({ payload: pagePayload, totalRecords: allOrders.length });
  }, [allOrders, pageNumber, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(allOrders.length / pageSize));
    if (pageNumber > maxPage) setPageNumber(1);
  }, [allOrders.length, pageSize, pageNumber]);

  const handleFilterChange = (selectedOption, params) => {
    if (params === "orderRef") setOrderRef(selectedOption);
    if (params === "project") setProject(selectedOption);
    if (params === "distributor") setDistributor(selectedOption);
    if (params === "customer") setCustomer(selectedOption);
  };

  const handleEdit = (item) => {
    if (item) navigate("/add-new-order", { state: { row: item, id: item?.id, canAdd: menuRights?.canAdd, canEdit: menuRights?.canEdit } });
  };

  const handleView = (item, projectDetails, distributorDetails, countryDetails, customerDetails, currencyDetails, assignedEngineerDetails) => {
    navigate('/view-order', { state: { item, canView: menuRights?.canView, projectDetails, distributorDetails, countryDetails, customerDetails, currencyDetails, assignedEngineerDetails } });
  };

  const ClearFilter = () => {
    setOrderRef(null); setDistributor(null); setCustomer(null); setProject(null);
    setAppliedFilters({ orderRef: null, project: null, customer: null, distributor: null });
    setFilterData({ PageNumber: 1, PageSize: filterData.PageSize, OrderReference: "", Project: "", Customer: "", Distributor: "", userId: filterData.userId, userType: filterData.userType });
    setPageNumber(1);
  };

  const ApplyFilter = (values = {}) => {
    setOrderRef(values.orderRef ?? orderRef);
    setProject(values.project ?? project);
    setCustomer(values.customer ?? customer);
    setDistributor(values.distributor ?? distributor);
    setAppliedFilters({
      orderRef: values.orderRef ?? orderRef,
      project: values.project ?? project,
      customer: values.customer ?? customer,
      distributor: values.distributor ?? distributor,
    });
    setFilterData((prev) => ({ ...prev, OrderReference: "", PageNumber: 1 }));
    setPageNumber(1);
    setSearch("");
  };

  const ApplySearch = () => {
    setPageNumber(1); setDistributor(null); setProject(null); setOrderRef(null); setCustomer(null);
    setAppliedFilters({ orderRef: null, project: null, customer: null, distributor: null });
    setFilterData({ PageNumber: 1, PageSize: filterData.PageSize, OrderReference: search, Customer: "", Distributor: "", Project: "", userId: filterData.userId, userType: filterData.userType });
  };

  const removeSearch = () => {
    setSearch(""); setPageNumber(1);
    setFilterData(prevData => ({ ...prevData, OrderReference: "", Customer: "", Distributor: "", Project: "", PageNumber: 1 }));
  };

  const RefreshList = () => {
    setSearch(""); setPageNumber(1); setPageSize(25);
    setDistributor(null); setCustomer(null); setProject(null); setOrderRef(null);
    setAppliedFilters({ orderRef: null, project: null, customer: null, distributor: null });
    setFilterData(initialFilterState);
  };

  const handleDelete = (item) => {
    setDeleteShow({ show: true, item, title: 'You want delete this Order?', textCode: 'Order Reference', text: item?.orderReference, loading: false });
  };

  const HandleCancelDelete = () => setDeleteShow({ show: false, item: "", title: '', textCode: '', text: '', loading: false });

  const HandleConfirmDelete = (item) => {
    setDeleteShow({ ...deleteShow, loading: true });
    DeleteEntry("order", item, setCreateMsg, SuccessMessages.DELETED("Order"), ErrorMessages.DELETED_ERROR("Order"));
  };

  const SuccessDoneClick = () => {
    setSuccessShow(false); setPageNumber(1);
    setFilterData((prev) => ({ ...prev, PageNumber: 1 }));
  };

  const ErrorDoneClick = () => { setErrorShow({ show: false, errorMessage: "" }); setCreateMsg(null); };

  useEffect(() => {
    if (createMsg?.successData === "Successful") {
      if (CheckNull(createMsg?.message) !== "") {
        const deletedOrderId = deleteShow?.item?.id;
        setDeleteShow({ show: false, item: "", title: '', textCode: '', text: '', loading: false });
        setSuccessShow(true);
        if (deletedOrderId != null) setAllOrders((prev) => prev.filter((item) => item.id !== deletedOrderId));
      }
    } else if (CheckNull(createMsg?.message) !== "") {
      setDeleteShow({ show: false, item: "", title: '', textCode: '', text: '', loading: false });
      setErrorShow({ show: true, errorMessage: createMsg.message });
      setSuccessShow(false);
    }
  }, [createMsg]);

  const handleKeyPress = (e) => { if (e.key === "Enter") ApplySearch(); };
  const addNew = () => navigate('/add-new-order', { state: { canAdd: menuRights?.canAdd, canEdit: menuRights?.canEdit } });
  const hasActiveFilters = Boolean(appliedFilters.orderRef || appliedFilters.project || appliedFilters.customer || appliedFilters.distributor);

  return (
    <>
      <section id="orderList">
        <div className="container-fluid">
          <div className="row">
            <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 px-0 py-1">
              <div className="card">
                <div className="card-body">
                  <div className="row search-area-row">
                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-4 col-sm-6 col-xs-12 my-auto p-0">
                      <div><p>{displayOrderList?.totalRecords ?? orderList?.totalRecords ?? 0} Results Found</p></div>
                    </div>
                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-8 col-sm-6 col-xs-12 my-auto p-0">
                      <SearchComponent
                        filteredResults={displayOrderList?.payload || orderList?.payload || []}
                        search={search}
                        handleKeyDown={handleKeyPress}
                        setSearch={setSearch}
                        removeSearch={removeSearch}
                        ApplySearch={ApplySearch}
                        RefreshList={RefreshList}
                        data={orderData?.payload || []}
                        addNew={addNew}
                        menuRights={menuRights}
                        hasActiveFilters={hasActiveFilters}
                        placeholder="Search by Customer Order Reference"
                        filterContent={(closeFilter) => (
                          <AdvancedFilterDropdown
                            ApplyFilter={ApplyFilter}
                            ClearFilter={ClearFilter}
                            startWithFirstOnly
                            onCloseFilter={closeFilter}
                            fields={[
                              { value: "orderRef", label: "Order Reference", placeholder: "Select order reference", pageName: "order", dpDownName: "orderRef", data: orderData?.payload, valueObject: orderRef, valueId: orderRef?.value, setState: setOrderRef, onChange: (e) => handleFilterChange(e, "orderRef") },
                              { value: "project", label: "Project", placeholder: "Select project", pageName: "order", dpDownName: "project", data: projectData, valueObject: project, valueId: project?.value, setState: setProject, onChange: (e) => handleFilterChange(e, "project") },
                              { value: "customer", label: "Customer", placeholder: "Select customer", pageName: "order", dpDownName: "customer", data: customerData, valueObject: customer, valueId: customer?.value, setState: setCustomer, onChange: (e) => handleFilterChange(e, "customer") },
                              { value: "distributor", label: "Distributor", placeholder: "Select distributor", pageName: "order", dpDownName: "distributor", data: distributorData, valueObject: distributor, valueId: distributor?.value, setState: setDistributor, onChange: (e) => handleFilterChange(e, "distributor") },
                            ]}
                          />
                        )} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <OrderSummary orderData={{ payload: allOrders }} />
          <OrderGrid
            loading={loading}
            orderList={displayOrderList || orderList}
            filterData={{ ...filterData, PageNumber: pageNumber, PageSize: pageSize }}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            pageSize={pageSize}
            distributorData={distributorData}
            projectData={projectData}
            countryData={countryData}
            customerData={customerData}
            currencyData={currencyData}
            assignedEngineerList={assignedEngineerList}
            handleEdit={handleEdit}
            handleView={handleView}
            handleDelete={handleDelete}
            menuRights={menuRights}
          />
          <DeleteModal deleteShow={deleteShow} handleCancelDelete={HandleCancelDelete} handleConfirmDelete={HandleConfirmDelete} />
          <ErrorMessage Show={errorShow?.show} errorMessage={errorShow?.errorMessage} ErrorDoneClick={ErrorDoneClick} />
          <SaveSuccessModel show={successShow} createMsg={createMsg} setShow={setSuccessShow} SuccessDoneClick={SuccessDoneClick} />
        </div>
      </section>
    </>
  );
};

export default OrderList;